import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Plus, Image as ImageIcon, Lock, Users, Globe, Trash2, Edit2, Share2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

function PhotoAlbums({ user }) {
  const navigate = useNavigate();
  const [albums, setAlbums] = useState([]);
  const [sharedAlbums, setSharedAlbums] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedAlbum, setSelectedAlbum] = useState(null);
  const [coverPhotoUrls, setCoverPhotoUrls] = useState({});
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    privacy_setting: 'private'
  });

  useEffect(() => {
    fetchAlbums();
  }, []);

  const getCoverPhotoUrl = async (albumId, photoId) => {
    try {
      const token = localStorage.getItem('token');
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      
      const response = await fetch(`${backendUrl}/api/photo-albums/${albumId}/photos/${photoId}/file`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        return null;
      }
      
      const blob = await response.blob();
      return URL.createObjectURL(blob);
    } catch (error) {
      console.error('Error fetching cover photo:', error);
      return null;
    }
  };

  const fetchAlbums = async () => {
    try {
      const token = localStorage.getItem('token');
      const backendUrl = process.env.REACT_APP_BACKEND_URL;

      // Fetch user's own albums
      const response = await fetch(`${backendUrl}/api/photo-albums`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('Failed to fetch albums');
      const data = await response.json();
      setAlbums(data);

      // Fetch albums shared with user
      const sharedResponse = await fetch(`${backendUrl}/api/photo-albums/shared-with-me`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      let sharedData = [];
      if (sharedResponse.ok) {
        sharedData = await sharedResponse.json();
        setSharedAlbums(sharedData);
      }
      
      // Stop loading indicator - show albums immediately
      setLoading(false);

      // Fetch cover photos in parallel (non-blocking, much faster!)
      const allAlbums = [...data, ...sharedData];
      const coverPhotoPromises = allAlbums
        .filter(album => album.cover_photo_id)
        .map(album => 
          getCoverPhotoUrl(album.id, album.cover_photo_id)
            .then(url => ({ albumId: album.id, url }))
            .catch(() => ({ albumId: album.id, url: null }))
        );

      // Load cover photos in parallel without blocking
      Promise.all(coverPhotoPromises).then(results => {
        const urls = {};
        results.forEach(({ albumId, url }) => {
          if (url) urls[albumId] = url;
        });
        setCoverPhotoUrls(urls);
      });

    } catch (error) {
      console.error('Error fetching albums:', error);
      toast.error('Failed to load albums');
      setLoading(false);
    }
  };

  const handleCreateAlbum = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const backendUrl = process.env.REACT_APP_BACKEND_URL;

      const response = await fetch(`${backendUrl}/api/photo-albums`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) throw new Error('Failed to create album');

      toast.success('Album created successfully!');
      setCreateDialogOpen(false);
      setFormData({ name: '', description: '', privacy_setting: 'private' });
      fetchAlbums();
    } catch (error) {
      console.error('Error creating album:', error);
      toast.error('Failed to create album');
    }
  };

  const handleUpdateAlbum = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const backendUrl = process.env.REACT_APP_BACKEND_URL;

      const response = await fetch(`${backendUrl}/api/photo-albums/${selectedAlbum.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) throw new Error('Failed to update album');

      toast.success('Album updated successfully!');
      setEditDialogOpen(false);
      setSelectedAlbum(null);
      setFormData({ name: '', description: '', privacy_setting: 'private' });
      fetchAlbums();
    } catch (error) {
      console.error('Error updating album:', error);
      toast.error('Failed to update album');
    }
  };

  const handleDeleteAlbum = async (albumId) => {
    if (!window.confirm('Are you sure? This will delete the album and all its photos.')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const backendUrl = process.env.REACT_APP_BACKEND_URL;

      const response = await fetch(`${backendUrl}/api/photo-albums/${albumId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('Failed to delete album');

      toast.success('Album deleted successfully!');
      fetchAlbums();
    } catch (error) {
      console.error('Error deleting album:', error);
      toast.error('Failed to delete album');
    }
  };

  const openEditDialog = (album) => {
    setSelectedAlbum(album);
    setFormData({
      name: album.name,
      description: album.description || '',
      privacy_setting: album.privacy_setting
    });
    setEditDialogOpen(true);
  };

  const getPrivacyIcon = (privacy) => {
    switch (privacy) {
      case 'public':
        return <Globe className="h-4 w-4 text-green-600" />;
      case 'team_visible':
        return <Users className="h-4 w-4 text-blue-600" />;
      default:
        return <Lock className="h-4 w-4 text-gray-600" />;
    }
  };

  const getPrivacyLabel = (privacy) => {
    switch (privacy) {
      case 'public':
        return 'Public';
      case 'team_visible':
        return 'Team Visible';
      default:
        return 'Private';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">My Photo Albums</h1>
          <p className="text-gray-600">Organize and share your construction project photos</p>
        </div>
      </div>

      {/* My Albums */}
      <div className="mb-12">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-gray-800">My Albums</h2>
          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700">
                <Plus className="h-5 w-5" />
                Create New Gallery
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create New Photo Gallery</DialogTitle>
                <DialogDescription>
                  Create a new photo gallery to organize your construction photos.
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateAlbum} className="space-y-4">
                <div>
                  <Label htmlFor="name">Gallery Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    placeholder="e.g., Summer Projects 2024"
                  />
                </div>
                <div>
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Add a description..."
                    rows={3}
                  />
                </div>
                <div>
                  <Label htmlFor="privacy">Privacy Setting</Label>
                  <Select
                    value={formData.privacy_setting}
                    onValueChange={(value) => setFormData({ ...formData, privacy_setting: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="private">Private - Only you can see</SelectItem>
                      <SelectItem value="team_visible">Team Visible - All team members can see</SelectItem>
                      <SelectItem value="public">Public - Everyone can see</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex gap-2">
                  <Button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700">
                    Create Gallery
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)}>
                    Cancel
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
        {albums.length === 0 ? (
          <div className="text-center py-16 bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl border-2 border-dashed border-blue-200">
            <div className="max-w-md mx-auto">
              <ImageIcon className="h-20 w-20 text-blue-400 mx-auto mb-6" />
              <h3 className="text-xl font-semibold text-gray-800 mb-3">Start Your Photo Collection</h3>
              <p className="text-gray-600 mb-6">Create your first photo gallery to organize and share your construction project photos.</p>
              <Button 
                onClick={() => setCreateDialogOpen(true)}
                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 text-lg"
              >
                <Plus className="h-6 w-6 mr-3" />
                Create Your First Gallery
              </Button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {albums.map((album) => (
              <div
                key={album.id}
                className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow cursor-pointer"
              >
                <div onClick={() => navigate(`/photo-albums/${album.id}`)}>
                  {coverPhotoUrls[album.id] ? (
                    <div className="h-48 bg-gray-100 relative overflow-hidden">
                      <img
                        src={coverPhotoUrls[album.id]}
                        alt={album.name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                      <div className="hidden h-full w-full bg-gradient-to-br from-blue-500 to-purple-600 items-center justify-center">
                        <ImageIcon className="h-20 w-20 text-white opacity-50" />
                      </div>
                    </div>
                  ) : (
                    <div className="h-48 bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                      <ImageIcon className="h-20 w-20 text-white opacity-50" />
                    </div>
                  )}
                  <div className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">{album.name}</h3>
                      <div className="flex items-center gap-1">
                        {getPrivacyIcon(album.privacy_setting)}
                      </div>
                    </div>
                    {album.description && (
                      <p className="text-sm text-gray-600 mb-2 line-clamp-2">{album.description}</p>
                    )}
                    <div className="flex items-center justify-between text-sm text-gray-500">
                      <span>{album.photo_count} photo{album.photo_count !== 1 ? 's' : ''}</span>
                      <span className="text-xs">{getPrivacyLabel(album.privacy_setting)}</span>
                    </div>
                  </div>
                </div>
                <div className="px-4 pb-4 flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className="flex-1"
                    onClick={() => openEditDialog(album)}
                  >
                    <Edit2 className="h-4 w-4 mr-1" />
                    Edit
                  </Button>
                  {user && user.role !== 'client' && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => navigate(`/photo-albums/${album.id}/share`)}
                    >
                      <Share2 className="h-4 w-4 mr-1" />
                      Share
                    </Button>
                  )}
                  {user && user.role === 'client' && (
                    <div className="text-xs text-green-600 font-medium px-2 py-1 bg-green-50 rounded">
                      Auto-shared with admins
                    </div>
                  )}
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => handleDeleteAlbum(album.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Shared with Me */}
      {sharedAlbums.length > 0 && (
        <div>
          <h2 className="text-xl font-semibold mb-4 text-gray-800">Shared with Me</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {sharedAlbums.map((album) => (
              <div
                key={album.id}
                className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow cursor-pointer"
                onClick={() => navigate(`/photo-albums/${album.id}`)}
              >
                {coverPhotoUrls[album.id] ? (
                  <div className="h-48 bg-gray-100 relative overflow-hidden">
                    <img
                      src={coverPhotoUrls[album.id]}
                      alt={album.name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                    <div className="hidden h-full w-full bg-gradient-to-br from-green-500 to-teal-600 items-center justify-center">
                      <ImageIcon className="h-20 w-20 text-white opacity-50" />
                    </div>
                  </div>
                ) : (
                  <div className="h-48 bg-gradient-to-br from-green-500 to-teal-600 flex items-center justify-center">
                    <ImageIcon className="h-20 w-20 text-white opacity-50" />
                  </div>
                )}
                <div className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="text-lg font-semibold text-gray-900">{album.name}</h3>
                    <Share2 className="h-4 w-4 text-blue-600" />
                  </div>
                  {album.description && (
                    <p className="text-sm text-gray-600 mb-2 line-clamp-2">{album.description}</p>
                  )}
                  <div className="flex items-center justify-between text-sm text-gray-500 mb-3">
                    <span>{album.photo_count} photo{album.photo_count !== 1 ? 's' : ''}</span>
                    <span className="text-xs">by {album.user_name}</span>
                  </div>
                  
                  {/* Admin actions for shared albums */}
                  {user && (user.role === 'admin' || user.role === 'supervisor') && (
                    <div className="flex gap-2 pt-2 border-t border-gray-200" onClick={(e) => e.stopPropagation()}>
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1 text-xs"
                        onClick={(e) => {
                          e.stopPropagation();
                          openEditDialog(album);
                        }}
                      >
                        <Edit2 className="h-3 w-3 mr-1" />
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1 text-xs"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/photo-albums/${album.id}/share`);
                        }}
                      >
                        <Share2 className="h-3 w-3 mr-1" />
                        Share
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        className="text-xs"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteAlbum(album.id);
                        }}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Floating Action Button for Create Gallery */}
      {albums.length > 0 && (
        <div className="fixed bottom-20 right-6 z-50">
          <Button
            onClick={() => setCreateDialogOpen(true)}
            className="h-14 w-14 rounded-full bg-blue-600 hover:bg-blue-700 shadow-lg hover:shadow-xl transition-all duration-200"
            title="Create New Gallery"
          >
            <Plus className="h-6 w-6" />
          </Button>
        </div>
      )}

      {/* Edit Album Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Album</DialogTitle>
            <DialogDescription>
              Update your album information.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdateAlbum} className="space-y-4">
            <div>
              <Label htmlFor="edit-name">Album Name *</Label>
              <Input
                id="edit-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="edit-description">Description</Label>
              <Textarea
                id="edit-description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
              />
            </div>
            <div>
              <Label htmlFor="edit-privacy">Privacy Setting</Label>
              <Select
                value={formData.privacy_setting}
                onValueChange={(value) => setFormData({ ...formData, privacy_setting: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="private">Private - Only you can see</SelectItem>
                  <SelectItem value="team_visible">Team Visible - All team members can see</SelectItem>
                  <SelectItem value="public">Public - Everyone can see</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2">
              <Button type="submit" className="flex-1">Update Album</Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setEditDialogOpen(false);
                  setSelectedAlbum(null);
                }}
              >
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default PhotoAlbums;
